import type { EIP1193Provider } from "viem";

import { useCallback, useEffect, useState } from "react";

import type { WalletState } from "../lib/wallet.js";

import { findChain } from "../data/tokens.js";
import { connectWallet, getInjectedProvider } from "../lib/wallet.js";
import styles from "../styles/WalletConnect.module.css";

/**
 * Wallet chip — sits in the header next to the title. Two responsibilities:
 *
 *   1. Render the connect/disconnect button + an address-chain badge.
 *   2. Push state changes up to the App via `onChange` so the rest of
 *      the playground (StepsExecutor in particular) can read the
 *      active address and chainId without prop-drilling the provider.
 *
 * `accountsChanged` / `chainChanged` listeners keep the badge fresh
 * when the user swaps wallets or networks from the wallet UI itself.
 */
export interface WalletConnectProps {
  readonly state: WalletState | undefined;
  readonly onChange: (state: WalletState | undefined) => void;
}

export function WalletConnect({
  state,
  onChange,
}: WalletConnectProps): React.JSX.Element {
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const provider = getInjectedProvider();

  const onClickConnect = useCallback(async () => {
    if (provider === undefined) {
      setError("No injected wallet found — install MetaMask, Rabby, etc.");
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const next = await connectWallet(provider);
      onChange(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [provider, onChange]);

  const onClickDisconnect = useCallback(() => {
    // Most injected wallets don't expose a disconnect RPC (their session
    // is managed in the wallet UI). Clearing local state is enough — the
    // user can revoke via wallet preferences when they want full logout.
    onChange(undefined);
  }, [onChange]);

  // Live updates from the injected provider.
  useEffect(() => {
    if (provider === undefined) return;
    const onAccountsChanged = (accounts: readonly string[]): void => {
      if (accounts.length === 0) {
        onChange(undefined);
        return;
      }
      onChange(
        state !== undefined
          ? { ...state, address: accounts[0] as `0x${string}` }
          : undefined,
      );
    };
    const onChainChanged = (chainIdHex: string): void => {
      if (state === undefined) return;
      onChange({ ...state, chainId: Number.parseInt(chainIdHex, 16) });
    };
    const eth = provider as EIP1193Provider & {
      on?: (event: string, listener: (...args: never[]) => void) => void;
      removeListener?: (
        event: string,
        listener: (...args: never[]) => void,
      ) => void;
    };
    eth.on?.("accountsChanged", onAccountsChanged as never);
    eth.on?.("chainChanged", onChainChanged as never);
    return () => {
      eth.removeListener?.("accountsChanged", onAccountsChanged as never);
      eth.removeListener?.("chainChanged", onChainChanged as never);
    };
  }, [provider, onChange, state]);

  if (state === undefined) {
    return (
      <div className={styles.wrap}>
        <button
          type="button"
          className={styles.connect}
          onClick={onClickConnect}
          disabled={busy || provider === undefined}
        >
          {busy ? "Conectando…" : "Conectar wallet"}
        </button>
        {error !== undefined && <span className={styles.error}>{error}</span>}
      </div>
    );
  }

  const chainInfo = findChain(state.chainId);
  const chainLabel =
    chainInfo !== undefined ? chainInfo.shortName : `Chain ${state.chainId}`;
  const short = `${state.address.slice(0, 6)}…${state.address.slice(-4)}`;

  return (
    <div className={styles.wrap}>
      <div className={styles.chip}>
        <span className={styles.chainLabel}>{chainLabel}</span>
        <span className={styles.addr}>{short}</span>
      </div>
      <button
        type="button"
        className={styles.disconnect}
        onClick={onClickDisconnect}
      >
        Desconectar
      </button>
    </div>
  );
}
